// useCrateWeightSamples — the active tenant's live crate-weight samples
// (mig 20261192500000), newest first. The key carries the tenant id so a tenant
// switch never serves the previous business's samples.
//
// The caller picks the estimate with sampleAvgFor(samples, variety, date) from
// '@/lib/crateWeightSamples'.

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { fetchAllPaged } from '@/lib/fetchAllPaged'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import type { CrateWeightSample } from '@/lib/crateWeightSamples'

type RawSample = Omit<CrateWeightSample, 'crates_weighed' | 'gross_kg' | 'avg_gross_per_crate'> & {
  crates_weighed: number | string
  gross_kg: number | string
  avg_gross_per_crate: number | string
}

export function useCrateWeightSamples() {
  const tenantId = useActiveTenantId()
  return useQuery<CrateWeightSample[]>({
    queryKey: ['crate-weight-samples', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      const { rows, error } = await fetchAllPaged<RawSample>((lo, hi) =>
        supabase
          .from('crate_weight_samples')
          .select('sample_id, variety, crate_type, sampled_on, crates_weighed, gross_kg, avg_gross_per_crate, note, created_at, deleted_at')
          .eq('tenant_id', tenantId!)
          .is('deleted_at', null)
          .order('sampled_on', { ascending: false }).order('created_at', { ascending: false }).order('sample_id')
          .range(lo, hi) as unknown as PromiseLike<{ data: RawSample[] | null; error: { message: string } | null }>,
      )
      if (error) throw new Error(error)
      return rows.map(r => ({
        ...r,
        crates_weighed: Number(r.crates_weighed),
        gross_kg: Number(r.gross_kg),
        avg_gross_per_crate: Number(r.avg_gross_per_crate),
      }))
    },
  })
}
